import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { BookingInquiryModal } from '../components/BookingInquiryModal';
import { SEOHead, BookingActionSchema, BreadcrumbSchema } from '../components/SEO';
import { 
  Loader2, Info, Gift, ExternalLink, Phone, CheckCircle, 
  Clock, Shield, Coffee, Percent, ArrowRight, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BOOKING_URL = 'https://thehotelemerald.bookingjini.in/';

const DIRECT_BENEFITS = [
  { icon: Percent, text: 'Up to 15% lower rates', highlight: true },
  { icon: Coffee, text: 'Complimentary breakfast' },
  { icon: Shield, text: 'Free cancellation (24h)' },
  { icon: Clock, text: 'Early check-in (subject to availability)' },
  { icon: Gift, text: 'Welcome drink on arrival' },
];

export default function BookingPage() {
  const [searchParams] = useSearchParams();
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [showIframe, setShowIframe] = useState(false);
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const [contact, setContact] = useState({
    phones: ['+91 6372 350 065'],
    whatsapp: '+916372350065'
  });

  const prefillRoom = searchParams.get('room');
  const source = searchParams.get('source') || 'booking_page';

  // Fetch contact info from site settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/site-settings`);
        if (response.data?.contact) {
          setContact(prev => ({ ...prev, ...response.data.contact }));
        }
      } catch (error) {
        console.warn('Failed to fetch settings:', error);
      }
    };
    fetchSettings();
  }, []);

  // Breadcrumbs for SEO
  const breadcrumbs = [
    { name: 'Home', url: 'https://thehotelemerald.in/' },
    { name: 'Book', url: 'https://thehotelemerald.in/book' }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="booking-page">
      <SEOHead 
        title="Book Your Stay | The Hotel Emerald - Best Rates Guaranteed"
        description="Book directly with The Hotel Emerald and save up to 15%. Get complimentary breakfast, flexible cancellation, and exclusive perks."
        keywords="hotel booking koraput, hotel emerald booking, semiliguda hotel reservation"
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <BookingActionSchema />
      <Navbar />
      
      {/* Header */}
      <section className="pt-28 sm:pt-32 pb-8 bg-gradient-to-b from-emerald-950 to-emerald-900">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white"
          >
            <Badge className="bg-gold text-emerald-950 mb-4">Best Price Guarantee</Badge>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl mb-3">
              Book Your Stay
            </h1>
            <p className="text-emerald-200 max-w-2xl mx-auto">
              Choose how you'd like to book - request a direct booking for exclusive savings 
              or book instantly through our booking partner.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Options */}
      <section className="py-12 bg-background">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
            
            {/* Option 1: Direct Booking Inquiry */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="relative bg-gradient-to-br from-emerald-900 to-emerald-800 rounded-2xl p-6 sm:p-8 text-white overflow-hidden"
            >
              {/* Recommended Badge */}
              <div className="absolute top-0 right-0 bg-gold text-emerald-950 px-4 py-1 rounded-bl-xl text-xs font-bold">
                RECOMMENDED
              </div>

              <div className="mb-6">
                <div className="w-14 h-14 rounded-2xl bg-gold/20 flex items-center justify-center mb-4">
                  <Gift className="w-7 h-7 text-gold" />
                </div>
                <h2 className="font-serif text-2xl mb-2">Book Direct & Save</h2>
                <p className="text-emerald-200 text-sm">
                  Request a direct booking and our team will contact you within 2 hours with exclusive rates.
                </p>
              </div>

              {/* Benefits */}
              <ul className="space-y-3 mb-6">
                {DIRECT_BENEFITS.map((benefit, idx) => (
                  <li key={idx} className={`flex items-center gap-3 text-sm ${benefit.highlight ? 'text-gold font-medium' : 'text-emerald-100'}`}>
                    <benefit.icon className={`w-4 h-4 flex-shrink-0 ${benefit.highlight ? 'text-gold' : 'text-emerald-400'}`} />
                    {benefit.text}
                  </li>
                ))}
              </ul>

              {/* Price Comparison */}
              <div className="bg-emerald-950/50 rounded-xl p-4 mb-6">
                <p className="text-xs text-emerald-300 mb-2">Example Savings (per night)</p>
                <div className="flex items-baseline gap-3">
                  <span className="text-2xl font-serif">₹8,500</span>
                  <span className="text-emerald-400 line-through">₹9,200</span>
                  <Badge className="bg-gold/20 text-gold border-gold/30">Save ₹700</Badge>
                </div>
              </div>

              <Button 
                onClick={() => setShowInquiryModal(true)}
                className="w-full btn-gold h-12 text-base"
                data-testid="book-direct-option"
              >
                Request Direct Booking
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <p className="text-xs text-emerald-300 text-center mt-3">
                <Phone className="w-3 h-3 inline mr-1" />
                We'll call you within 2 hours
              </p>
            </motion.div>

            {/* Option 2: Instant Booking */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-card border border-border rounded-2xl p-6 sm:p-8"
            >
              <div className="mb-6">
                <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mb-4">
                  <Zap className="w-7 h-7 text-foreground" />
                </div>
                <h2 className="font-serif text-2xl text-foreground mb-2">Instant Booking</h2>
                <p className="text-muted-foreground text-sm">
                  Book immediately through our booking partner. Instant confirmation, standard rates.
                </p>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-sm text-foreground">
                  <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  Instant confirmation
                </li>
                <li className="flex items-center gap-3 text-sm text-foreground">
                  <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  Pay now or at hotel
                </li>
                <li className="flex items-center gap-3 text-sm text-foreground">
                  <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  All room types available
                </li>
                <li className="flex items-center gap-3 text-sm text-muted-foreground">
                  <Info className="w-4 h-4 flex-shrink-0" />
                  Standard rates (no direct booking discounts)
                </li>
              </ul>

              <div className="flex flex-col gap-3">
                <Button 
                  onClick={() => setShowIframe(true)}
                  variant="outline"
                  className="w-full h-12 text-base"
                  data-testid="instant-book-option"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Book Instantly
                </Button>

                <a 
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-muted-foreground text-center hover:text-foreground transition-colors"
                >
                  Or open in new tab →
                </a>
              </div>
            </motion.div>
          </div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-12 text-center"
          >
            <p className="text-sm text-muted-foreground mb-4">Trusted by guests from across India</p>
            <div className="flex flex-wrap justify-center gap-6 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Shield className="w-4 h-4" /> Secure Booking
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4" /> Best Price Guarantee
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" /> 24/7 Support
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Iframe Section (shown when user clicks Instant Book) */}
      {showIframe && (
        <section className="bg-white border-t">
          <div className="container-custom py-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-lg text-foreground">Complete Your Booking</h3>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowIframe(false)}
              >
                Hide Booking Widget
              </Button>
            </div>
          </div>
          
          <div className="relative min-h-[700px] bg-white">
            {isIframeLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-background z-10">
                <div className="text-center">
                  <Loader2 className="w-10 h-10 text-emerald-700 animate-spin mx-auto mb-3" />
                  <p className="text-muted-foreground text-sm">Loading booking system...</p>
                </div>
              </div>
            )}
            
            <iframe
              src={BOOKING_URL}
              title="Book Your Stay at The Hotel Emerald"
              className="w-full border-0"
              style={{ height: '700px', minHeight: '700px' }}
              onLoad={() => setIsIframeLoading(false)}
              allow="payment"
              data-testid="booking-iframe"
            />
          </div>
        </section>
      )}

      {/* Need Help Section */}
      <section className="py-12 bg-muted/50">
        <div className="container-custom text-center">
          <h3 className="font-serif text-xl text-foreground mb-2">Need Help Booking?</h3>
          <p className="text-muted-foreground text-sm mb-4">
            Our team is available 24/7 to assist you
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a 
              href={`tel:${contact.phones?.[0]?.replace(/\s/g, '') || '+916372350065'}`} 
              className="btn-emerald inline-flex items-center justify-center gap-2"
            >
              <Phone className="w-4 h-4" />
              Call {contact.phones?.[0] || '+91 6372 350 065'}
            </a>
            <a 
              href={`https://wa.me/${(contact.whatsapp || '+916372350065').replace(/[^0-9]/g, '')}?text=Hello! I would like to book a room at The Hotel Emerald.`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold inline-flex items-center justify-center gap-2"
            >
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      <Footer />

      {/* Booking Inquiry Modal */}
      <BookingInquiryModal 
        open={showInquiryModal} 
        onOpenChange={setShowInquiryModal}
        prefillRoom={prefillRoom}
        source={source}
      />
    </div>
  );
}
